/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Species force-field table for the Molecular Modeling engine: experiment-fit
 * non-bonded parameters (Lennard-Jones σ, ε and point charges) for the handful
 * of small molecules whose phase behavior the labs measure and quote — the
 * noble gases, N₂, O₂, CO₂, methane and water.
 *
 * WHY. Every atom in the lab starts with the UFF Lennard-Jones parameters of its
 * element (uffParameters.ts). UFF was built for coverage of the periodic table
 * and reasonable geometries, never for condensed-phase thermodynamics: its argon
 * well (ε/k ≈ 93 K) is 22 % shallower than the measured one, so an argon pool
 * boils ~25 % too cold, and its N₂ is too weakly bound to hold a liquid at all.
 * The models below were each fit to measured liquid–vapor data (densities,
 * heats of vaporization, coexistence curves), so a boiling-point or
 * vapor-pressure reading from the lab lands near the handbook value instead of
 * needing a per-example patch.
 *
 * HOW. The table is keyed by MOLECULE, not by element: a nitrogen atom gets the
 * N₂ parameters only when it belongs to a bare N≡N molecule; inside an amine it
 * keeps UFF. applySpeciesForceField() runs in the MolecularDynamics constructor
 * (after the general parameters, before the first force evaluation), so every
 * engine build — the lab's mirror, the worker's copy, headless tools, seeders —
 * gets the same species. Unrecognized molecules fall through to UFF untouched.
 * Cross-species pairs keep the engine's Lorentz–Berthelot mixing, which is what
 * these models assume.
 *
 * LIMITS (documented so nobody re-derives them):
 *  - N₂ and O₂ default to their two-center Lennard-Jones models, with no
 *    charges. The full TraPPE models carry the molecular quadrupole as a
 *    positive massless charge at the bond center balanced by negative charges
 *    on the atoms; they are in each entry's `quadrupole` and turn on with
 *    ProjectState.quadrupoleSites (MolecularDynamics.setQuadrupoleSites). Off by
 *    default because the saved nitrogen examples were calibrated without them.
 *  - Water stays TIP3P (waterModel.ts): the two live water examples and the
 *    evaporation tools' quoted numbers were calibrated on it. SPC/E is tabulated
 *    below as the drop-in alternative (same three sites, melts at ~215 K vs
 *    TIP3P's ~146 K) and switching is a one-line change in WATER.sites — after
 *    the water examples are re-run.
 *  - Helium: a classical Lennard-Jones helium still boils high (~7 K vs 4.2 K)
 *    because quantum effects dominate real helium; the Aziz-era well is still
 *    far closer than UFF's 28 K.
 *
 * Units: σ in Å, ε in eV (converted from the sources' K or kcal/mol), charge in e.
 * Verified by speciesForceField.test.ts (npm run test:speciesff).
 */

import type { Atom } from './Atom.ts';
import type { Molecule } from './Molecule.ts';
import { UNIT_EV_OVER_KB } from './physicalConstants.ts';
import { TIP3P } from './waterModel.ts';

export const KCAL_PER_MOL_TO_EV = 0.0433641;
/** ε/k_B in kelvin → ε in eV, on the engine's own temperature scale (UNIT_EV_OVER_KB). */
export const kelvinToEv = (epsilonOverKb: number): number => epsilonOverKb / UNIT_EV_OVER_KB;
export const evToKelvin = (epsilonEv: number): number => epsilonEv * UNIT_EV_OVER_KB;

/** One Lennard-Jones / charge site. `sigma` omitted = keep the atom's own σ (a charge-only site). */
export interface SpeciesSite {
  sigma?: number; // Å
  epsilon: number; // eV
  charge: number; // e
}

export interface SpeciesEntry {
  /** Hill formula with UPPERCASE element symbols: 'AR', 'N2', 'CO2', 'CH4', 'H2O'. */
  key: string;
  name: string;
  /** The published model and where its parameters were fit. */
  source: string;
  /** The measured quantity the lab reads that this entry is meant to reproduce. */
  reference: string;
  /** Element every other atom must be bonded to (omit for a monatomic species). */
  hub?: string;
  /**
   * A monatomic SOLID: any number of unbonded atoms of the one element in a
   * molecule match (a fixed metal layer the lab uses as a substrate). Without
   * this a hub-less entry matches a single atom only.
   */
  lattice?: boolean;
  /** Bonding distance (Å) used when the molecule carries no radial bonds. */
  bondReach: number;
  /** Per-element site parameters, keyed by UPPERCASE symbol. */
  sites: Record<string, SpeciesSite>;
  /**
   * The full model with a massless charge at the bond center (a homonuclear
   * diatomic only): it replaces `sites` while the lab's quadrupole option is on.
   * The atoms' charges and `centerCharge` sum to zero.
   */
  quadrupole?: {
    source: string;
    sites: Record<string, SpeciesSite>;
    centerCharge: number; // e
  };
}

const lj = (sigma: number, epsilonOverKbK: number, charge = 0): SpeciesSite => ({
  sigma,
  epsilon: kelvinToEv(epsilonOverKbK),
  charge,
});

const nobleGas = (symbol: string, name: string, sigma: number, epsK: number, source: string, boilingK: number) =>
  ({
    key: symbol,
    name,
    source,
    reference: `normal boiling point ${boilingK} K`,
    bondReach: 0,
    sites: { [symbol]: lj(sigma, epsK) },
  }) satisfies SpeciesEntry;

/**
 * SPC/E water (Berendsen, Grigera & Straatsma, J. Phys. Chem. 91, 6269 (1987)):
 * the three-site alternative to TIP3P with a credible ice (melting point ≈ 215 K
 * vs TIP3P's ≈ 146 K). Not active — see the file header.
 */
export const SPC_E = {
  chargeO: -0.8476,
  chargeH: 0.4238,
  sigmaO: 3.166, // Å
  epsilonO: 0.1553 * KCAL_PER_MOL_TO_EV, // 0.650 kJ/mol
} as const;

export const SPECIES_FORCE_FIELD: readonly SpeciesEntry[] = [
  nobleGas('HE', 'helium', 2.556, 10.22, 'de Boer & Michels (1938) Lennard-Jones helium; classical limit', 4.2),
  nobleGas('NE', 'neon', 2.78, 35.6, 'Hirschfelder, Curtiss & Bird (1954), second virial coefficients', 27.1),
  nobleGas('AR', 'argon', 3.405, 119.8, 'Michels, Wijker & Wijker (1949) / Rahman (1964) liquid argon', 87.3),
  nobleGas('KR', 'krypton', 3.63, 167.0, 'Hirschfelder, Curtiss & Bird (1954), second virial coefficients', 119.9),
  nobleGas('XE', 'xenon', 4.1, 221.0, 'Hirschfelder, Curtiss & Bird (1954), second virial coefficients', 165.1),
  {
    key: 'N2',
    name: 'nitrogen',
    source:
      'Cheung & Powles, Mol. Phys. 30, 921 (1975): two-center Lennard-Jones fit to liquid N₂ (TraPPE adds a center-site quadrupole the engine cannot host)',
    reference: 'normal boiling point 77.4 K, ΔH_vap 5.6 kJ/mol',
    hub: 'N',
    bondReach: 1.3,
    sites: { N: lj(3.31, 37.3) },
    quadrupole: {
      source:
        'TraPPE N₂, Potoff & Siepmann, AIChE J. 47, 1676 (2001): two LJ centers, −0.482 e on each N, +0.964 e at the bond center',
      sites: { N: lj(3.31, 36.0, -0.482) },
      centerCharge: 0.964,
    },
  },
  {
    key: 'O2',
    name: 'oxygen',
    source:
      'TraPPE O₂, Zhang & Siepmann, Theor. Chem. Acc. 115, 391 (2006): two-center Lennard-Jones (its center-site charges left out)',
    reference: 'normal boiling point 90.2 K, ΔH_vap 6.8 kJ/mol',
    hub: 'O',
    bondReach: 1.4,
    sites: { O: lj(3.02, 49.0) },
    quadrupole: {
      source:
        'TraPPE O₂, Zhang & Siepmann, Theor. Chem. Acc. 115, 391 (2006): −0.113 e on each O, +0.226 e at the bond center',
      sites: { O: lj(3.02, 49.0, -0.113) },
      centerCharge: 0.226,
    },
  },
  {
    key: 'CO2',
    name: 'carbon dioxide',
    source: 'TraPPE CO₂, Potoff & Siepmann, AIChE J. 47, 1676 (2001): three sites, charges on the atoms',
    reference: 'sublimation point 194.7 K, critical point 304.1 K',
    hub: 'C',
    bondReach: 1.4,
    sites: { C: lj(2.8, 27.0, 0.7), O: lj(3.05, 79.0, -0.35) },
  },
  {
    key: 'CH4',
    name: 'methane',
    source: 'OPLS-AA methane, Jorgensen, Maxwell & Tirado-Rives, J. Am. Chem. Soc. 118, 11225 (1996)',
    reference: 'normal boiling point 111.7 K, ΔH_vap 8.2 kJ/mol',
    hub: 'C',
    bondReach: 1.3,
    sites: {
      C: { sigma: 3.5, epsilon: 0.066 * KCAL_PER_MOL_TO_EV, charge: -0.24 },
      H: { sigma: 2.5, epsilon: 0.03 * KCAL_PER_MOL_TO_EV, charge: 0.06 },
    },
  },
  {
    // A GOLD SURFACE: the fixed Au(111) layer a nanocar or a fullerene rolls on
    // (nano/nanocar.ts). UFF's gold well (0.0017 eV) is a van der Waals
    // placeholder under which a C₆₀ would leave the surface at room temperature;
    // measured C₆₀ on Au(111) is chemisorbed by roughly 1–1.5 eV per molecule.
    // ε is set so the Lorentz–Berthelot Au–C well (√(ε_Au·ε_C) with UFF carbon,
    // ≈ 0.025 eV) sums over a fullerene's contact to about 1 eV. σ is UFF's.
    key: 'AU',
    name: 'gold surface',
    source:
      'σ from UFF (Rappé et al. 1992); ε chosen for ~1 eV C₆₀ adsorption on Au(111), the measured order (Altman & Colton 1993)',
    reference: 'a fullerene that stays on the surface at 300 K and can roll along it',
    lattice: true,
    bondReach: 0,
    sites: { AU: { sigma: 2.934, epsilon: 0.14, charge: 0 } },
  },
  {
    key: 'H2O',
    name: 'water',
    source: 'TIP3P, Jorgensen et al., J. Chem. Phys. 79, 926 (1983) — see waterModel.ts',
    reference: 'liquid density and ΔH_vap at 298 K; a liquid at 300 K that evaporates as it heats',
    hub: 'O',
    bondReach: 1.3,
    sites: {
      O: { sigma: TIP3P.sigmaO, epsilon: TIP3P.epsilonO, charge: TIP3P.chargeO },
      // Hydrogens carry charge only; σ is kept for the neighbor list.
      H: { epsilon: 0, charge: TIP3P.chargeH },
    },
  },
];

const BY_KEY: ReadonlyMap<string, SpeciesEntry> = new Map(SPECIES_FORCE_FIELD.map((e) => [e.key, e]));

/** The table entry for a Hill-formula key ('AR', 'CO2', …), if any. */
export const speciesEntry = (key: string): SpeciesEntry | undefined => BY_KEY.get(key.toUpperCase());

/** The site parameters a lone atom of `elementSymbol` gets from the table (noble gases), or undefined. */
export const speciesSiteForElement = (elementSymbol: string): SpeciesSite | undefined => {
  const sym = elementSymbol.toUpperCase();
  const e = BY_KEY.get(sym);
  return e && !e.hub ? e.sites[sym] : undefined;
};

const symbolOf = (a: Atom): string => (a.elementSymbol ?? '').toUpperCase();

/**
 * Hill formula of a molecule with UPPERCASE symbols: carbon first, hydrogen
 * second, the rest alphabetical ('CH4', 'CO2', 'H2O', 'N2', 'AR').
 */
export const speciesKeyOf = (m: Molecule): string => {
  const counts = new Map<string, number>();
  for (const a of m.atoms) {
    const s = symbolOf(a);
    counts.set(s, (counts.get(s) ?? 0) + 1);
  }
  const rank = (s: string) => (s === 'C' ? 0 : s === 'H' ? 1 : 2);
  const symbols = [...counts.keys()].sort((a, b) => rank(a) - rank(b) || (a < b ? -1 : a > b ? 1 : 0));
  let key = '';
  for (const s of symbols) {
    const n = counts.get(s)!;
    key += n > 1 ? s + n : s;
  }
  return key;
};

const bonded = (m: Molecule, a: Atom, b: Atom, reach: number): boolean => {
  if (m.radialBonds && m.radialBonds.length > 0) {
    return m.radialBonds.some((rb) => (rb.atom1 === a && rb.atom2 === b) || (rb.atom1 === b && rb.atom2 === a));
  }
  return a.position.distanceTo(b.position) < reach;
};

/**
 * The table entry a molecule matches, or null. The formula must match exactly
 * and, for a polyatomic entry, some atom of the hub element must be bonded to
 * every other atom (radial bonds when the molecule has them, distance otherwise)
 * — so CO₂ is O=C=O and not a C and two O that happen to share a molecule.
 */
export const speciesEntryFor = (m: Molecule): SpeciesEntry | null => {
  if (!m.atoms || m.atoms.length === 0) return null;
  // A monatomic SOLID (lattice entry) matches by element alone: the Hill key of
  // a 200-atom gold layer is 'AU200', which no entry carries.
  const elements = new Set(m.atoms.map(symbolOf));
  if (elements.size === 1) {
    const solid = BY_KEY.get([...elements][0]);
    if (solid?.lattice) return solid;
  }
  const e = BY_KEY.get(speciesKeyOf(m));
  if (!e) return null;
  if (!e.hub) return m.atoms.length === 1 || e.lattice ? e : null;
  const hubs = m.atoms.filter((a) => symbolOf(a) === e.hub);
  const ok = hubs.some((hub) => m.atoms.every((a) => a === hub || bonded(m, hub, a, e.bondReach)));
  return ok ? e : null;
};

/**
 * Give every recognized molecule its species parameters: σ and ε on each site,
 * and the model's charge unless the user set that atom's charge by hand
 * (Atom.chargeSet). Idempotent; returns how many molecules were set.
 */
export const applySpeciesForceField = (molecules: Molecule[]): number => {
  let n = 0;
  for (const m of molecules) {
    const e = speciesEntryFor(m);
    if (!e) continue;
    for (const a of m.atoms) {
      const site = e.sites[symbolOf(a)];
      if (!site) continue;
      if (site.sigma !== undefined) a.sigma = site.sigma;
      a.epsilon = site.epsilon;
      if (!a.chargeSet) a.charge = site.charge;
    }
    n++;
  }
  return n;
};

/**
 * The quadrupole model a molecule takes when the lab's option is on, or null:
 * a recognized N₂ / O₂ whose two atoms are joined by a radial bond (so the
 * kernel already excludes their own pair) and whose charges nobody set by
 * hand — a user-set charge keeps the molecule on the user's numbers, since a
 * hidden center charge would then leave it non-neutral.
 */
export const quadrupoleModelFor = (m: Molecule): SpeciesEntry['quadrupole'] | null => {
  const e = speciesEntryFor(m);
  if (!e?.quadrupole || m.atoms.length !== 2) return null;
  if (!m.radialBonds || m.radialBonds.length === 0) return null;
  if (m.atoms.some((a) => a.chargeSet)) return null;
  return e.quadrupole;
};

/**
 * Put a molecule on its quadrupole model's LJ and atom charges (on, and the
 * model applies) or back on the default entry (otherwise — also when a hand-set
 * charge has just taken a molecule off the model: its partner atom returns to
 * the two-site values). The center site itself is the engine's
 * (MolecularDynamics.setQuadrupoleSites). Returns whether the molecule needs a
 * center site.
 */
export const applyQuadrupoleModel = (m: Molecule, on: boolean): boolean => {
  const q = on ? quadrupoleModelFor(m) : null;
  if (!q) {
    if (speciesEntryFor(m)?.quadrupole) applySpeciesForceField([m]);
    return false;
  }
  for (const a of m.atoms) {
    const site = q.sites[symbolOf(a)];
    if (!site) continue;
    if (site.sigma !== undefined) a.sigma = site.sigma;
    a.epsilon = site.epsilon;
    a.charge = site.charge;
  }
  return true;
};
